import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import {
  assertVolunteerOrgContactPairs,
  emptyToNull,
  isVolunteerOrgBranch,
  namesConflict,
} from "@shared/helpers/volunteer-organization";
import { Roles } from "@shared/Roles";
import { remult } from "remult";
import { VolunteerOrgProjectAssignment } from "../volunteer-org-project-assignment/entity";
import { VolunteerOrganization } from "./entity";
import type { VolunteerOrganization as VolunteerOrganizationType } from "./entity";

function callCenterAccess(allow: (roles: typeof Roles.AdminsAndManager, value: true) => void) {
  allow(Roles.AdminsAndManager, true);
}

export const VolunteerOrganizationEntityConfig: HyperEntityConfig<VolunteerOrganizationType> =
  {
    allowApiRead: true,
    allowApiCrud: Roles.AdminsAndManager,

    apiPrefilterPerRole: (filter) => {
      if (!remult.user) return;
      filter(Roles.AdminsAndManager, true);
      filter(Roles.User, false);
    },

    allowInsertPerRole: callCenterAccess,
    allowDeleteAndUpdatePerRole: callCenterAccess,

    async saving(entity) {
      entity.name = entity.name?.trim() ?? "";
      if (!entity.name) {
        throw new Error("שם הארגון חובה");
      }
      if (entity.name.length > 200) {
        throw new Error("שם הארגון ארוך מדי");
      }

      const isSuper = remult.isAllowed(Roles.SuperAdmins);
      if (entity.id && !isSuper) {
        const previous = await remult.repo(VolunteerOrganization).findId(entity.id);
        if (
          previous?.handlingManagerId &&
          previous.handlingManagerId !== remult.user?.id
        ) {
          throw new Error("רק המנהל המטפל יכול לעדכן ארגון זה");
        }
      }

      entity.description = emptyToNull(entity.description);
      entity.relationshipType = emptyToNull(entity.relationshipType);
      entity.comments = entity.comments?.trim() ?? "";
      assertVolunteerOrgContactPairs(entity);

      const parentId = emptyToNull(entity.parentId);
      entity.parentId = parentId;

      if (parentId) {
        if (parentId === entity.id) {
          throw new Error("לא ניתן לשייך ארגון לעצמו");
        }
        const parent = await remult.repo(VolunteerOrganization).findId(parentId);
        if (!parent) {
          throw new Error("ארגון האב לא נמצא");
        }
        if (isVolunteerOrgBranch(parent)) {
          throw new Error("לא ניתן לשייך שלוחה לשלוחה");
        }
        const childCount = entity.id
          ? await remult.repo(VolunteerOrganization).count({ parentId: entity.id })
          : 0;
        if (childCount > 0) {
          throw new Error(
            "לא ניתן להפוך ארגון אב לשלוחה כל עוד יש לו שלוחות",
          );
        }
      } else if (entity.id) {
        const previous = await remult.repo(VolunteerOrganization).findId(entity.id);
        if (previous && isVolunteerOrgBranch(previous)) {
          const assignmentCount = await remult
            .repo(VolunteerOrgProjectAssignment)
            .count({ volunteerOrganizationId: entity.id });
          if (assignmentCount > 0) {
            throw new Error(
              "לא ניתן להפוך שלוחה לארגון עצמאי כל עוד יש לה שיבוצים",
            );
          }
        }
      }

      entity.address = emptyToNull(entity.address);
      if (parentId && !entity.address) {
        throw new Error("כתובת חובה לשלוחה");
      }
      if (
        entity.volunteerCapacity != null &&
        (!Number.isFinite(entity.volunteerCapacity) ||
          entity.volunteerCapacity < 0)
      ) {
        throw new Error("קיבולת מתנדבים לא יכולה להיות שלילית");
      }
      if (entity.volunteerCapacity != null) {
        entity.volunteerCapacity = Math.floor(entity.volunteerCapacity);
      }

      const siblings = await remult.repo(VolunteerOrganization).find({
        where: parentId ? { parentId } : { parentId: null },
        limit: 5000,
      });
      if (
        siblings.some(
          (row) => row.id !== entity.id && namesConflict(row.name, entity.name),
        )
      ) {
        throw new Error(
          parentId
            ? "כבר קיימת שלוחה בשם זה תחת הארגון האב"
            : "כבר קיים ארגון בשם זה",
        );
      }
    },

    async deleting(entity) {
      const isSuper = remult.isAllowed(Roles.SuperAdmins);
      if (!isSuper) {
        throw new Error("אין הרשאה למחוק ארגון מתנדבים");
      }
      if (isVolunteerOrgBranch(entity)) return;
      const childCount = await remult
        .repo(VolunteerOrganization)
        .count({ parentId: entity.id });
      if (childCount > 0) {
        throw new Error("לא ניתן למחוק ארגון אב כל עוד יש לו שלוחות");
      }
    },
  };
